import React, { useEffect } from 'react';
import { NavigationProp, useNavigation } from '@react-navigation/native';
import { LPRParamList } from '../../../typings/Params';
import Filters from '../../../components/Filter';
import { useState } from 'react';
import moment from 'moment';
import FilterDatePicker from '../../../components/Filter/components/FilterDateInput';
import { translate } from '../../../services/translate';
import CamerasDropdown from '../../../components/lpr/CamerasDropdown';
import { View } from 'react-native';
import FilterInput from '../../../components/Filter/components/FilterInput';
import UnitNamesDropdown from '../../../components/lpr/UnitNamesDropdown';
import { useAuth } from '../../../context/Auth';
import TypesDropdown from '../../../components/lpr/TypesDropdown';
import { useForm } from 'react-hook-form';
import { MyCheckbox } from '../../../components/MyCheckbox';

interface Props {
    filters: LPRParamList['LprSearch'];
    setFilters: React.Dispatch<React.SetStateAction<LPRParamList['LprSearch']>>;
    plateFilter: string;
    setPlateFilter: React.Dispatch<React.SetStateAction<string>>;
}

interface FiltersForm {
    beginInDateFilter: Date | null;
    beginInHourFilter: string;
    endInDateFilter: Date | null;
    endInHourFilter: string;
    selectedCamerasIds: string[];
    selectedUnitNamesIds: number[];
    irregularSituationFilter: boolean;
    isMotorcycleFilter: boolean;
    type: string;
}

export default function LprSearchFilter({ filters, setFilters, setPlateFilter, plateFilter }: Props) {
    const navigation = useNavigation<NavigationProp<LPRParamList, 'LprSearch'>>();
    const { user: authenticatedUser } = useAuth();

    const defaultEmptyValues: FiltersForm = {
        beginInDateFilter: null,
        beginInHourFilter: '',
        endInDateFilter: null,
        endInHourFilter: '',
        selectedCamerasIds: [],
        selectedUnitNamesIds: [],
        irregularSituationFilter: false,
        isMotorcycleFilter: false,
        type: ''
    };

    const form = useForm<FiltersForm>({
        defaultValues: defaultEmptyValues,
        mode: 'onChange'
    });

    const [estimatedStartZIndex, setEstimatedStartZIndex] = useState<number>(6);
    const [estimatedFinishZIndex, setEstimatedFinishZIndex] = useState<number>(5);

    useEffect(() => {
        form.reset({
            beginInDateFilter: filters.beginIn ? new Date(filters.beginIn) : null,
            beginInHourFilter: filters.beginIn ? moment(filters.beginIn).format('HH:mm') : '',
            endInDateFilter: filters.endIn ? new Date(filters.endIn) : null,
            endInHourFilter: filters.endIn ? moment(filters.endIn).format('HH:mm') : '',
            selectedCamerasIds: filters.cameras ?? [],
            selectedUnitNamesIds: filters.unitNames ?? [],
            irregularSituationFilter: filters.irregularSituation ?? false,
            isMotorcycleFilter: filters.isMotorcycle ?? false,
            type: filters.type || ''
        });

    }, []);

    const handleCleanFilter = () => {
        setPlateFilter('');
        form.reset(defaultEmptyValues);
    };

    const handleFilter = () => {
        const values = form.getValues();

        const formatDateTime = (date: Date | null, time: string) =>
            date ? moment(`${moment(date).format('DD/MM/YYYY')} ${time}`, 'DD/MM/YYYY HH:mm:ss').valueOf() : undefined;
        const beginIn = formatDateTime(values.beginInDateFilter, values.beginInHourFilter ? `${values.beginInHourFilter}:00` : '00:00:00');
        const endIn = formatDateTime(values.endInDateFilter, values.endInHourFilter ? `${values.endInHourFilter}:59` : '23:59:59');

        setFilters({
            ...filters,
            cameras: values.selectedCamerasIds.length ? values.selectedCamerasIds : undefined,
            unitNames: values.selectedUnitNamesIds.length ? values.selectedUnitNamesIds : undefined,
            beginIn: beginIn && !isNaN(beginIn) ? beginIn : undefined,
            endIn: endIn && !isNaN(endIn) ? endIn : undefined,
            plate: plateFilter !== '' ? plateFilter.toUpperCase() : undefined,
            irregularSituation: values.irregularSituationFilter,
            isMotorcycle: values.isMotorcycleFilter,
            type: values.type && values.type !== '' ? values.type : undefined
        });

        navigation.setParams({
            cameras: values.selectedCamerasIds.length ? values.selectedCamerasIds : undefined,
            unitNames: values.selectedUnitNamesIds.length ? values.selectedUnitNamesIds : undefined,
            beginIn: beginIn && !isNaN(beginIn) ? beginIn : undefined,
            endIn: endIn && !isNaN(endIn) ? endIn : undefined,
            plate: plateFilter !== '' ? plateFilter.toUpperCase() : undefined,
            irregularSituation: values.irregularSituationFilter,
            isMotorcycle: values.isMotorcycleFilter,
            type: values.type && values.type !== '' ? values.type : undefined
        });
    };

    return (
        <Filters onCleanFilters={handleCleanFilter} onFilters={handleFilter}>
            <FilterInput
                label={translate('plate')}
                value={plateFilter}
                placeholder={translate('typeToSearch')}
                onChange={setPlateFilter}
            />
            <View style={{ zIndex: estimatedStartZIndex }}>
                <FilterDatePicker
                    form={form}
                    dateFieldName='beginInDateFilter'
                    timeFieldName='beginInHourFilter'
                    label={translate('beginIn')}
                    zIndex={6}
                    onCalendarOpen={() => setEstimatedStartZIndex(99999)}
                    onCalendarClose={() => setEstimatedStartZIndex(6)}
                />
            </View>
            <View style={{ zIndex: estimatedFinishZIndex }}>
                <FilterDatePicker
                    form={form}
                    dateFieldName='endInDateFilter'
                    timeFieldName='endInHourFilter'
                    label={translate('endIn')}
                    zIndex={5}
                    onCalendarOpen={() => setEstimatedFinishZIndex(99999)}
                    onCalendarClose={() => setEstimatedFinishZIndex(5)}
                />
            </View>
            <View style={{ flexDirection: 'row', justifyContent: 'space-between' }}>
                <MyCheckbox
                    label={translate('irregularSituation')}
                    form={form}
                    fieldName='irregularSituationFilter'
                />
                <MyCheckbox
                    label={translate('motorcycleOnly')}
                    form={form}
                    fieldName='isMotorcycleFilter'
                />
            </View>
            <TypesDropdown
                form={form}
                fieldName='type'
                zIndex={5}
            />
            <CamerasDropdown
                showLabel={true}
                fieldName='selectedCamerasIds'
                form={form}
                zIndex={4}
            />
            {(authenticatedUser?.permissions.lpr_watch_unit_detections || authenticatedUser?.permissions.lpr_can_watch_all_events) &&
                <UnitNamesDropdown
                    showLabel={true}
                    form={form}
                    fieldName='selectedUnitNamesIds'
                    zIndex={3}
                />
            }
        </Filters>
    );
}
